import { useState } from "react";
import { apiClient, useApiQuery, type Api } from "../../../services/api";
import { type RequestOutput } from "../../../../../api/src/shared/request";
import { type QuestionOrigin, type RouterPrediction } from "./useGptChat";
import { type CurrentUser } from "../../user/hooks/useCurrentUser";
import { currentClient } from "../../../common/hooks/useIsDesktopView";

// Client deadline for a blocking Evidence turn — just under Cloud Run's 300 s
// edge timeout (the request layer would otherwise cancel it at its 60 s default).
const EVIDENCE_TURN_TIMEOUT_MS = 290_000;

// The blocking-turn wire shapes, inferred from the api contract (never a custom
// shape). A conversational turn returns the same gated graph as `/ask`, plus
// where it landed in the thread; the hydrate GET returns every past turn.
export type EvidenceTurnBody = RequestOutput<
  Api["post"]["/webapp/private/evidence-gpt/turn"]
>["body"];
type EvidenceHydrateBody = RequestOutput<
  Api["get"]["/webapp/private/evidence-gpt/conversations/:id"]
>["body"];
export type EvidenceConversationTurn = EvidenceHydrateBody["turns"][number];

// Just the answer content a turn renders (both the live turn body and a
// hydrated turn's `answer` are assignable to this — the shared `/ask` shape).
// `droppedEdges` is what the Evidence details tab shows as "excluded by
// verification" — the audit trail, not part of the prose answer.
export type EvidenceGraphView = Pick<
  EvidenceTurnBody,
  "answer" | "sources" | "claims" | "juisciGrade" | "edges" | "droppedEdges"
>;
export type EvidenceSourceView = EvidenceGraphView["sources"][number];

// A rendered turn: an answered graph, a still-in-flight question, or one whose
// turn failed. Discriminated so the surface never reads an absent answer.
export type EvidenceChatTurn =
  | {
      status: "answered";
      turnIndex: number;
      question: string;
      answer: EvidenceGraphView;
    }
  | { status: "pending"; question: string }
  | { status: "error"; question: string };

// The result of a send: the (possibly newly minted) conversation id and whether
// this send CREATED the thread — the surface navigates to the real url on the
// first turn so a reload/back finds it.
type SendResult = { conversationId: string; created: boolean } | null;

/**
 * The Evidence GPT conversational runtime for the webapp. Unlike the general
 * chat (`useGptChat`, a streaming `useChat`), a turn here is BLOCKING: one
 * `POST /turn` computes the whole gated graph and returns it. This hook owns the
 * turn list (server history + this session's sends) and the in-flight question.
 *
 * A fresh thread starts with `conversationId: null`; the first send mints it
 * server-side (returned by `send`), and the surface navigates to the real url.
 */
export function useEvidenceGptChat(args: {
  conversationId: string | null;
  includeWeb: boolean;
  language: CurrentUser["language"];
  // Whether this hook OWNS history hydration. `true` on the standalone Evidence
  // surface. `false` in the unified Mode Router surface (docs/gpt-mode-router.md):
  // there the conversation is `juisci_gpt_page`, whose evidence turns are served
  // by the GENERAL hydrate's `evidenceAnswers` side-car — the Evidence-only
  // hydrate endpoint would 409 on that origin — so the hook is used ONLY to send
  // and to hold this session's live turns.
  hydrate: boolean;
}) {
  const [conversationId, setConversationId] = useState(args.conversationId);
  // Turns added THIS session (the blocking sends). Keyed into the merged list by
  // turnIndex so a hydrate refetch can't double them.
  const [localTurns, setLocalTurns] = useState<
    { turnIndex: number; question: string; answer: EvidenceGraphView }[]
  >([]);
  const [pending, setPending] = useState<string | null>(null);
  const [failed, setFailed] = useState<string | null>(null);

  // Hydrate the thread's history whenever we have an id. A follow-up appends to
  // `localTurns` and is merged in below (deduped by turnIndex), so a hydrate
  // refetch never doubles this session's sends.
  const hydrateQuery = useApiQuery(
    "/webapp/private/evidence-gpt/conversations/:id",
    conversationId === null || !args.hydrate
      ? false
      : { param: { id: conversationId } }
  );
  const isHydrating =
    conversationId !== null && hydrateQuery.state === "pending";

  const serverTurns: EvidenceConversationTurn[] =
    hydrateQuery.state === "success" ? hydrateQuery.body.turns : [];

  // Merge server history + this session's sends, deduped by turnIndex (a
  // follow-up wins over a stale hydrated copy), then chronological.
  const byIndex = new Map<
    number,
    { turnIndex: number; question: string; answer: EvidenceGraphView }
  >();
  for (const turn of serverTurns)
    byIndex.set(turn.turnIndex, {
      turnIndex: turn.turnIndex,
      question: turn.question,
      answer: turn.answer,
    });
  for (const turn of localTurns) byIndex.set(turn.turnIndex, turn);
  const answered: EvidenceChatTurn[] = [...byIndex.values()]
    .sort((a, b) => a.turnIndex - b.turnIndex)
    .map((turn) => ({ status: "answered", ...turn }));

  const turns: EvidenceChatTurn[] =
    pending !== null
      ? [...answered, { status: "pending", question: pending }]
      : failed !== null
        ? [...answered, { status: "error", question: failed }]
        : answered;

  async function send(
    question: string,
    origin: QuestionOrigin,
    // Non-null ONLY when the Mode Router redispatched an Auto turn here
    // (docs/gpt-mode-router.md): the classifier prediction, so the persisted turn
    // is labelled Auto→evidence. `null` on a direct forced-Evidence send.
    autoRouterPrediction: RouterPrediction | null
  ): Promise<SendResult> {
    const wasNew = conversationId === null;
    setFailed(null);
    setPending(question);
    try {
      const { body } = await apiClient.post(
        "/webapp/private/evidence-gpt/turn",
        {
          json: {
            conversationId,
            question,
            includeWeb: args.includeWeb,
            language: args.language,
            origin,
            autoRouterPrediction,
            client: currentClient(),
          },
          // A blocking Evidence turn (graph construction + synthesis) legitimately
          // runs minutes; the request layer's 60 s default would cancel it. Give it
          // a deadline just under Cloud Run's 300 s edge (past which the server is
          // killed anyway).
          signal: AbortSignal.timeout(EVIDENCE_TURN_TIMEOUT_MS),
        }
      );
      setPending(null);
      setLocalTurns((prev) => [
        ...prev,
        {
          turnIndex: body.turnIndex,
          question,
          answer: body,
        },
      ]);
      if (wasNew) setConversationId(body.conversationId);
      return { conversationId: body.conversationId, created: wasNew };
    } catch {
      // A blocking turn that failed: surface the question as an error row so the
      // user can retry, and drop the pending state.
      setPending(null);
      setFailed(question);
      return null;
    }
  }

  return {
    conversationId,
    turns,
    isHydrating,
    isSending: pending !== null,
    send,
  };
}
